import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField
} from "@mui/material";
import React from "react";

import { THINKING_MODE_TEMPERATURE } from "../../../services/claude";
import { useChatStore } from "../stores/chatStore";
import AhaLensesButton from "./AhaLensesButton";
import ChatInputStatus from "./ChatInputStatus";

interface ChatInputProps {
  loading: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

const EDITING_SX = {
  bgcolor: "rgba(25, 118, 210, 0.05)",
  borderColor: "primary.main"
} as const;

const TOO_LARGE_SX = {
  bgcolor: "rgba(211, 47, 47, 0.05)",
  borderColor: "error.main"
} as const;

interface PlaceholderParams {
  viewingPreviousSession: boolean;
  isContextDataTooLarge: boolean;
  editingMessage: boolean;
}

const getPlaceholder = ({ viewingPreviousSession, isContextDataTooLarge, editingMessage }: PlaceholderParams): string => {
  if (viewingPreviousSession) {
    return "This is a read-only view of a previous session";
  }
  if (isContextDataTooLarge) {
    return "Please reduce context data before sending messages...";
  }
  if (editingMessage) {
    return "Edit your question...";
  }
  return "Ask a question about the data...";
};

interface StartAdornmentProps {
  onSelectQuestion: (question: string) => void;
}

const StartAdornment = ({ onSelectQuestion }: StartAdornmentProps): React.ReactElement => (
  <InputAdornment position="start">
    <AhaLensesButton onSelectQuestion={onSelectQuestion} />
  </InputAdornment>
);

interface EndAdornmentProps {
  editingMessage: boolean;
  isProcessing: boolean;
  inputValue: string;
  inputDisabled: boolean;
  cancelEditMessage: () => void;
  stopGeneration: () => void;
  handleSend: () => void;
}

const EndAdornment = (params: EndAdornmentProps): React.ReactElement => {
  const { editingMessage, isProcessing, inputValue, inputDisabled, cancelEditMessage, stopGeneration, handleSend } = params;
  return (
    <InputAdornment position="end">
      {editingMessage && (
        <IconButton color="default" onClick={cancelEditMessage} sx={{ mr: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      {isProcessing ? (
        <IconButton color="error" onClick={stopGeneration} aria-label="Stop generation">
          <StopIcon />
        </IconButton>
      ) : (
        <IconButton color="primary" onClick={handleSend} disabled={!inputValue.trim() || inputDisabled}>
          <SendIcon />
        </IconButton>
      )}
    </InputAdornment>
  );
};

interface SendHandlerParams {
  inputValue: string;
  inputDisabled: boolean;
  thinkingMode: boolean;
  temperature: number;
  isChunkingEnabled: boolean;
  startChunkingProcess: (params: { content: string; temperature: number }) => Promise<void>;
  sendMessage: (content: string, temperature: number) => Promise<void>;
}

const buildSendHandler = (params: SendHandlerParams) => async (): Promise<void> => {
  const { inputValue, inputDisabled, thinkingMode, temperature, isChunkingEnabled, startChunkingProcess, sendMessage } = params;
  if (!inputValue.trim() || inputDisabled) {
    return;
  }
  const customTemperature = thinkingMode ? THINKING_MODE_TEMPERATURE : temperature;
  if (isChunkingEnabled) {
    await startChunkingProcess({ content: inputValue, temperature: customTemperature });
    return;
  }
  await sendMessage(inputValue, customTemperature);
};

interface KeyHandlerParams {
  inputDisabled: boolean;
  editingMessage: boolean;
  handleSend: () => void;
  cancelEditMessage: () => void;
}

const handleEnterKey = (e: React.KeyboardEvent, inputDisabled: boolean, handleSend: () => void): void => {
  e.preventDefault();
  if (!inputDisabled) {
    handleSend();
  }
};

const buildKeyHandler = (params: KeyHandlerParams) => (e: React.KeyboardEvent): void => {
  const { inputDisabled, editingMessage, handleSend, cancelEditMessage } = params;
  if (e.key === "Enter" && !e.shiftKey) {
    handleEnterKey(e, inputDisabled, handleSend);
    return;
  }
  if (e.key === "Escape" && editingMessage) {
    cancelEditMessage();
  }
};

interface TextFieldSxParams {
  editingMessage: boolean;
  isContextDataTooLarge: boolean;
}

const buildTextFieldSx = ({ editingMessage, isContextDataTooLarge }: TextFieldSxParams): object => ({
  mb: 1,
  ["& .MuiOutlinedInput-root"]: {
    ...(editingMessage && EDITING_SX),
    ...(isContextDataTooLarge && TOO_LARGE_SX)
  }
});

interface ChatInputFieldProps {
  inputValue: string;
  inputDisabled: boolean;
  isContextDataTooLarge: boolean;
  isProcessing: boolean;
  editingMessage: boolean;
  viewingPreviousSession: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  setInputValue: (v: string) => void;
  cancelEditMessage: () => void;
  stopGeneration: () => void;
  handleSend: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
}

const ChatInputField = (props: ChatInputFieldProps): React.ReactElement => {
  const {
    inputValue, inputDisabled, isContextDataTooLarge, isProcessing, editingMessage, viewingPreviousSession,
    inputRef, setInputValue, cancelEditMessage, stopGeneration, handleSend, handleKeyPress
  } = props;
  return (
    <TextField
      fullWidth
      multiline
      maxRows={4}
      placeholder={getPlaceholder({ viewingPreviousSession, isContextDataTooLarge, editingMessage })}
      value={viewingPreviousSession ? "" : inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onKeyDown={handleKeyPress}
      disabled={inputDisabled}
      inputRef={inputRef}
      slotProps={{ input: {
        startAdornment: <StartAdornment onSelectQuestion={setInputValue} />,
        endAdornment: (
          <EndAdornment
            editingMessage={editingMessage}
            isProcessing={isProcessing}
            inputValue={inputValue}
            inputDisabled={inputDisabled}
            cancelEditMessage={cancelEditMessage}
            stopGeneration={stopGeneration}
            handleSend={handleSend}
          />
        )
      } }}
      sx={buildTextFieldSx({ editingMessage, isContextDataTooLarge })}
    />
  );
};

const ChatInput = ({ loading, inputRef }: ChatInputProps): React.ReactElement => {
  const {
    inputValue,
    editingMessage,
    thinkingMode,
    temperature,
    isBlockedByTokenLimit,
    setInputValue,
    sendMessage,
    cancelEditMessage,
    stopGeneration,
    isChunkingEnabled,
    startChunkingProcess,
    isCreatingChunks,
    viewingPreviousSession
  } = useChatStore();

  const isContextDataTooLarge = isBlockedByTokenLimit && !isChunkingEnabled;
  const isProcessing = loading || isCreatingChunks;
  const inputDisabled = isProcessing || isContextDataTooLarge || viewingPreviousSession;

  const handleSend = buildSendHandler({
    inputValue, inputDisabled, thinkingMode, temperature, isChunkingEnabled, startChunkingProcess, sendMessage
  });

  const handleKeyPress = buildKeyHandler({
    inputDisabled, editingMessage: Boolean(editingMessage), handleSend, cancelEditMessage
  });

  return (
    <Box sx={{ p: 2, bgcolor: "#ffffff" }}>
      <ChatInputField
        inputValue={inputValue}
        inputDisabled={inputDisabled}
        isContextDataTooLarge={isContextDataTooLarge}
        isProcessing={isProcessing}
        editingMessage={Boolean(editingMessage)}
        viewingPreviousSession={viewingPreviousSession}
        inputRef={inputRef}
        setInputValue={setInputValue}
        cancelEditMessage={cancelEditMessage}
        stopGeneration={stopGeneration}
        handleSend={handleSend}
        handleKeyPress={handleKeyPress}
      />
      <ChatInputStatus loading={loading} />
    </Box>
  );
};

export default ChatInput;
